import { useCallback, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import type { Organization, OrgUnitType } from '@/api/orgAdmin';
import type { ToastApi } from '@/components/ui';
import {
    checkCrossRoot,
    checkDrop,
    checkInactiveTarget,
    checkSelfDescendant,
    checkUnitTypeContainment,
    findNode,
} from './dragRules';
import type {
    DragSource,
    DragTarget,
    DropMode,
    IllegalDropInfo,
    OrganizationTreeNode,
} from './types';

/**
 * EAA-1818: drag + keyboard state machine for the hierarchy tree, split out of
 * `useHierarchyPage` so that hook stays focused on data/CRUD.
 *
 * A RE-PARENT ENDPOINT NOW EXISTS. Earlier revisions of this file documented the
 * opposite — `PATCH /organizations/{id}` had no `parent` key, so a legal drop
 * could only ever surface an honest inline failure rather than fake a success.
 * EAA-1836 shipped that key, so `completeDrop` now hands the move to
 * `useOrgMove.requestMove`, which raises frame 29's confirm dialog and performs
 * the real write on confirmation.
 *
 * WHAT AN INSERTION LINE MEANS HERE. It is a re-parent, not a reorder — read the
 * chip in frame 28: "vào Trường A – K12", i.e. the *destination parent*, never an
 * index. That is the only reading the backend can honour: `Organization` has no
 * sibling-order column and `OrganizationService.list_children` sorts by name, so
 * position within a level is not something the client can persist. Dropping
 * before/after row R therefore re-parents the dragged org to R's OWN parent, and
 * every legality rule is graded against that resolved parent rather than against
 * the row the pointer happens to be over.
 *
 * UNIT-TYPE CONTAINMENT (EAA-1838) is graded here too, best effort:
 * `evaluateTarget` runs `checkUnitTypeContainment` against the RESOLVED
 * destination parent, like every other rule, so dropping "before Khối 1–5" is
 * judged on Khối 1–5's parent's type rather than on Khối 1–5's own. It never
 * objects when a type is missing or unloaded on either side.
 *
 * LEVELS REMAIN UNMOVABLE, honestly. There is no ordering field on `Level`
 * either, and a Level has no parent at all (see `types.ts`), so a Level drag can
 * only ever report that the reorder cannot be saved. Levels stay draggable so
 * frame 29's "Level không kéo vào đơn vị" feedback is reachable at all.
 */

export interface ResolvedDropParent {
    parentId: string;
    parentName: string;
}

export interface UseHierarchyDragArgs {
    tree: OrganizationTreeNode[];
    rootOrgId: string | null;
    knownOrgs: Map<string, Organization>;
    /**
     * EAA-1838: the root's unit-type vocabulary by id, for the containment drop
     * rule. An EMPTY map is a legitimate state (not loaded yet) and simply means
     * the rule never objects — see `dragRules.checkUnitTypeContainment`.
     */
    unitTypesById: Map<string, OrgUnitType>;
    levelNames: Map<string, string>;
    visibleOrgIds: string[];
    visibleLevelIds: string[];
    /** Raises the confirm dialog — see `useOrgMove`. */
    requestMove: (
        node: OrganizationTreeNode,
        targetId: string | null,
        targetName: string,
        origin: 'drag' | 'dialog'
    ) => void;
    /**
     * EAA-1891: hand a CROSS-TREE drop to the adoption dialog instead of failing
     * it. The drag path itself cannot perform this move — it writes `PATCH parent`,
     * which the server still refuses across trees, and it migrates no unit-type
     * vocabulary — but the move is supported now, so rejecting the gesture
     * outright would teach the admin the wrong thing. Optional: without it the
     * drop reports the rule as before.
     */
    onCrossTreeDrop?: (
        node: OrganizationTreeNode,
        targetId: string,
        targetName: string
    ) => void;
    /** False for org-admins: the server rejects a parent change from them. */
    canMove: boolean;
    toast: ToastApi;
}

function rowZoneFromPointer(clientY: number, rect: DOMRect, allowOnto: boolean): DropMode {
    const offset = (clientY - rect.top) / rect.height;
    if (allowOnto) {
        if (offset < 0.25) return 'before';
        if (offset > 0.75) return 'after';
        return 'onto';
    }
    return offset < 0.5 ? 'before' : 'after';
}

export function useHierarchyDrag({
    tree,
    rootOrgId,
    knownOrgs,
    unitTypesById,
    levelNames,
    visibleOrgIds,
    visibleLevelIds,
    requestMove,
    onCrossTreeDrop,
    canMove,
    toast,
}: UseHierarchyDragArgs) {
    const [dragSource, setDragSource] = useState<DragSource | null>(null);
    const [dragTarget, setDragTarget] = useState<DragTarget | null>(null);
    const [illegal, setIllegal] = useState<IllegalDropInfo | null>(null);
    const [keyboardActive, setKeyboardActive] = useState(false);
    const [inlineDropError, setInlineDropError] = useState<string | null>(null);
    const [announcement, setAnnouncement] = useState('');
    const rafRef = useRef<number | null>(null);
    /**
     * EAA-1804: last target actually committed to `dragTarget` state, so
     * `setDragTargetAndCheck` can bail out when the pointer is still hovering
     * the same row segment — see the function below.
     */
    const lastTargetRef = useRef<DragTarget | null>(null);

    const orgName = useCallback((id: string) => knownOrgs.get(id)?.name ?? 'đơn vị', [knownOrgs]);

    /**
     * Turn a hovered row + mode into the parent the org would actually end up
     * under. `null` means the position is not expressible as a re-parent — the
     * one case being before/after the ROOT row, which would move the org out of
     * the acting org's tree entirely (see `useOrgTree`'s root decision).
     */
    const resolveDropParent = useCallback(
        (target: DragTarget): ResolvedDropParent | null => {
            if (target.kind !== 'org') return null;
            if (target.mode === 'onto') {
                return { parentId: target.id, parentName: orgName(target.id) };
            }
            const node = findNode(tree, target.id);
            const parentId = node?.organization.parent ?? null;
            if (!node || node.depth === 0 || !parentId) return null;
            return { parentId, parentName: orgName(parentId) };
        },
        [tree, orgName]
    );

    const evaluateTarget = useCallback(
        (source: DragSource, target: DragTarget): IllegalDropInfo | null => {
            const structural = checkDrop({ source, target });
            if (structural) return structural;
            if (source.kind !== 'org' || target.kind !== 'org') return null;

            const resolved = resolveDropParent(target);
            if (!resolved) {
                return {
                    reason: 'Không thể chuyển đơn vị ra ngoài tổ chức đang thao tác',
                    tone: 'danger',
                };
            }
            if (resolved.parentId === source.id) {
                return { reason: 'Không thể chuyển một đơn vị vào chính nó', tone: 'danger' };
            }

            const sourceOrg = knownOrgs.get(source.id) ?? null;
            const parentOrg = knownOrgs.get(resolved.parentId) ?? null;
            if (sourceOrg && sourceOrg.parent === resolved.parentId) {
                return {
                    reason: `"${source.label}" đã ở trong ${resolved.parentName}`,
                    tone: 'neutral',
                };
            }
            // EAA-1838: containment is graded LAST of the four organization rules,
            // so the more specific structural failures (cycle, inactive, cross-root)
            // keep naming themselves rather than being masked by "loại đơn vị không
            // hợp" — a target in another tree is a different problem from a target
            // whose type cannot hold this one, even when both are true.
            return (
                checkSelfDescendant(tree, source.id, resolved.parentId) ??
                checkInactiveTarget(parentOrg) ??
                (sourceOrg ? checkCrossRoot(sourceOrg, resolved.parentId, knownOrgs) : null) ??
                (sourceOrg
                    ? checkUnitTypeContainment(sourceOrg, parentOrg, unitTypesById)
                    : null)
            );
        },
        [tree, knownOrgs, unitTypesById, resolveDropParent]
    );

    const labelForTarget = useCallback(
        (target: DragTarget): string => {
            if (target.kind === 'org') return orgName(target.id);
            return levelNames.get(target.id) ?? 'Level';
        },
        [orgName, levelNames]
    );

    /**
     * The chip text on the insertion line and the screen-reader announcement are
     * intentionally the same string: both must name the DESTINATION PARENT, so
     * "before Khối 1–5" reads as "vào Trường A – K12".
     */
    const describeSlot = useCallback(
        (target: DragTarget): string => {
            if (target.kind === 'level') return 'Thả vào Level của tổ chức';
            const resolved = resolveDropParent(target);
            if (!resolved) return 'Không thả được ở đây';
            return `Thả vào ${resolved.parentName}`;
        },
        [resolveDropParent]
    );

    const setDragTargetAndCheck = useCallback(
        (source: DragSource, target: DragTarget) => {
            /**
             * EAA-1804: bail out when the newly computed target is identical to
             * the one already committed. `onRowDragOver` recomputes a target on
             * every `requestAnimationFrame` tick while the pointer moves, so
             * sliding within the SAME row segment (no zone/row change) used to
             * fire `setDragTarget` + `setIllegal` + `setAnnouncement`
             * unconditionally on every frame — three state updates driving a
             * full `HierarchyTreePanel` re-render (tree panel, detail panel and
             * the six closed dialog components) per mousemove. Comparing the
             * three fields that define a slot (kind/id/mode) — not a deep
             * `JSON.stringify` — lets repeated frames over the same slot skip
             * all three updates entirely.
             */
            const last = lastTargetRef.current;
            if (
                last &&
                last.kind === target.kind &&
                last.id === target.id &&
                last.mode === target.mode
            ) {
                return;
            }
            lastTargetRef.current = target;
            setDragTarget(target);
            const result = evaluateTarget(source, target);
            setIllegal(result);
            const label = describeSlot(target);
            setAnnouncement(
                result
                    ? `${labelForTarget(target)} — không thể thả: ${result.reason}`
                    : `Vị trí mới: ${label}`
            );
        },
        [evaluateTarget, describeSlot, labelForTarget]
    );

    const cancelDrag = useCallback(() => {
        setDragSource((current) => {
            if (current) setAnnouncement(`Đã bỏ di chuyển ${current.label}`);
            return null;
        });
        setDragTarget(null);
        setIllegal(null);
        setKeyboardActive(false);
        // EAA-1804: clear the bail-out cache too, so the NEXT drag's first
        // target — even if it lands back on the same slot as the one this
        // drag ended on — is not silently swallowed by setDragTargetAndCheck.
        lastTargetRef.current = null;
    }, []);

    /** Ordered drop slots for keyboard Arrow navigation. */
    const buildSlots = useCallback(
        (kind: 'org' | 'level'): DragTarget[] => {
            const rows = kind === 'org' ? visibleOrgIds : visibleLevelIds;
            const slots: DragTarget[] = [];
            rows.forEach((id, i) => {
                slots.push({ kind, id, mode: 'before' });
                if (kind === 'org') slots.push({ kind, id, mode: 'onto' });
                if (i === rows.length - 1) slots.push({ kind, id, mode: 'after' });
            });
            return slots;
        },
        [visibleOrgIds, visibleLevelIds]
    );

    const beginKeyboardDrag = useCallback(
        (source: DragSource) => {
            setDragSource(source);
            setKeyboardActive(true);
            const slots = buildSlots(source.kind);
            const startSlot =
                slots.find((s) => s.id === source.id && s.mode !== 'before') ?? slots[0] ?? null;
            if (startSlot) setDragTargetAndCheck(source, startSlot);
            setAnnouncement(
                `Đã nhấc ${source.label} để di chuyển. Dùng phím mũi tên để chọn vị trí.`
            );
        },
        [buildSlots, setDragTargetAndCheck]
    );

    const moveKeyboardTarget = useCallback(
        (direction: 1 | -1) => {
            if (!dragSource) return;
            const slots = buildSlots(dragSource.kind);
            const currentIndex = dragTarget
                ? slots.findIndex((s) => s.id === dragTarget.id && s.mode === dragTarget.mode)
                : -1;
            const nextIndex = Math.min(Math.max(currentIndex + direction, 0), slots.length - 1);
            const next = slots[nextIndex];
            if (next) setDragTargetAndCheck(dragSource, next);
        },
        [dragSource, dragTarget, buildSlots, setDragTargetAndCheck]
    );

    const completeDrop = useCallback(() => {
        if (!dragSource || !dragTarget) {
            cancelDrag();
            return;
        }
        if (illegal) {
            /**
             * EAA-1891: a cross-tree drop is not a dead end any more. The gesture
             * cannot be honoured as-is (this path writes `PATCH parent`, which the
             * server refuses across trees, and it would leave the moved subtree
             * pointing at its old root's unit-type vocabulary), so it escalates to
             * the adoption dialog — which previews the blast radius and the
             * vocabulary merge before anything is written. Deliberately NOT a
             * silent redirect into a write: the drop opens a dialog the admin has
             * to confirm, because "I dragged a node" is not consent to
             * restructure two trees.
             */
            if (illegal.rule === 'cross-root' && onCrossTreeDrop && canMove) {
                const crossNode = findNode(tree, dragSource.id);
                const crossTarget = resolveDropParent(dragTarget);
                if (crossNode && crossTarget) {
                    onCrossTreeDrop(crossNode, crossTarget.parentId, crossTarget.parentName);
                    setInlineDropError(null);
                    cancelDrag();
                    return;
                }
            }
            setAnnouncement(`Không thể thả — ${illegal.reason}`);
            cancelDrag();
            return;
        }

        // A Level drag can never be saved: no ordering column, no parent.
        if (dragSource.kind === 'level') {
            const message = `Không thể lưu thứ tự Level "${dragSource.label}" — Level không có thứ tự lưu được trong tổ chức. Không có gì bị thay đổi.`;
            setInlineDropError(message);
            toast.error(message);
            setAnnouncement(message);
            cancelDrag();
            return;
        }

        const node = findNode(tree, dragSource.id);
        const resolved = resolveDropParent(dragTarget);
        if (!node || !resolved) {
            cancelDrag();
            return;
        }
        if (!canMove) {
            const message = 'Chỉ super-admin đổi được cấp cha của đơn vị. Không có gì bị thay đổi.';
            setInlineDropError(message);
            toast.error(message);
            cancelDrag();
            return;
        }
        setInlineDropError(null);
        requestMove(node, resolved.parentId, resolved.parentName, 'drag');
        cancelDrag();
    }, [
        dragSource,
        dragTarget,
        illegal,
        tree,
        resolveDropParent,
        requestMove,
        onCrossTreeDrop,
        canMove,
        cancelDrag,
        toast,
    ]);

    /**
     * Normalizes an "after row_i" pointer hover into "before row_{i+1}" so the
     * same physical gap is never represented two different ways. Only collapses
     * within the same depth: `visibleOrgIds` is a depth-first walk, so the next
     * row may sit at a different level, and the two gaps then resolve to
     * different parents and must stay distinct.
     */
    const normalizeMode = useCallback(
        (kind: 'org' | 'level', id: string, mode: DropMode): DragTarget => {
            if (mode !== 'after') return { kind, id, mode };
            const rows = kind === 'org' ? visibleOrgIds : visibleLevelIds;
            const idx = rows.indexOf(id);
            if (idx < 0 || idx >= rows.length - 1) return { kind, id, mode: 'after' };
            const nextId = rows[idx + 1];
            if (kind === 'org') {
                const here = findNode(tree, id);
                const next = findNode(tree, nextId);
                if (!here || !next || here.depth !== next.depth) return { kind, id, mode: 'after' };
            }
            return { kind, id: nextId, mode: 'before' };
        },
        [visibleOrgIds, visibleLevelIds, tree]
    );

    const onRowDragStart = useCallback((source: DragSource) => {
        setDragSource(source);
        setKeyboardActive(false);
        setAnnouncement(`Đang di chuyển ${source.label}`);
    }, []);

    const onRowDragOver = useCallback(
        (rowId: string, rowKind: 'org' | 'level', rect: DOMRect, clientY: number) => {
            if (!dragSource) return;
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            rafRef.current = requestAnimationFrame(() => {
                // The root row accepts 'onto' only — before/after it would mean
                // leaving the acting org's tree (see resolveDropParent).
                const isRootRow = rowKind === 'org' && rowId === rootOrgId;
                const allowOnto = rowKind === 'org' && dragSource.kind === 'org';
                const mode =
                    isRootRow && allowOnto ? 'onto' : rowZoneFromPointer(clientY, rect, allowOnto);
                setDragTargetAndCheck(dragSource, normalizeMode(rowKind, rowId, mode));
            });
        },
        [dragSource, rootOrgId, normalizeMode, setDragTargetAndCheck]
    );

    const onRowDrop = useCallback(() => completeDrop(), [completeDrop]);
    const onRowDragEnd = useCallback(() => cancelDrag(), [cancelDrag]);

    const onTreeKeyDown = useCallback(
        (e: KeyboardEvent, source: DragSource) => {
            if (!dragSource) {
                if (e.key === ' ' || e.key === 'Spacebar') {
                    e.preventDefault();
                    beginKeyboardDrag(source);
                }
                return;
            }
            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    moveKeyboardTarget(1);
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    moveKeyboardTarget(-1);
                    break;
                case 'Enter':
                case ' ':
                case 'Spacebar':
                    e.preventDefault();
                    completeDrop();
                    break;
                case 'Escape':
                    e.preventDefault();
                    cancelDrag();
                    break;
                default:
                    break;
            }
        },
        [dragSource, beginKeyboardDrag, moveKeyboardTarget, completeDrop, cancelDrag]
    );

    /** Chip label for the currently hovered insertion line. */
    const dropChipLabel = useMemo(
        () => (dragTarget ? describeSlot(dragTarget) : ''),
        [dragTarget, describeSlot]
    );

    return {
        dragSource,
        dragTarget,
        illegal,
        keyboardActive,
        inlineDropError,
        setInlineDropError,
        announcement,
        dropChipLabel,
        onRowDragStart,
        onRowDragOver,
        onRowDrop,
        onRowDragEnd,
        onTreeKeyDown,
        cancelDrag,
    };
}

export type HierarchyDragVM = ReturnType<typeof useHierarchyDrag>;
